import React, { useEffect, useState } from 'react';
import { fetchWrapper } from '@/lib/fetch-wrapper';
import { urls } from '@/urls';
import {
  Modal,
  Backdrop,
  Fade,
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import { Pill } from '@/components/ds';
import { statusTone } from '@/theme/tokens';

interface BudgetChangeModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit?: (data: BudgetChangeData) => void;
  budgetType: 'global' | 'department' | 'system';
  currentBudget: number;
  budgetCurrency: string;
  entityName?: string;
  departmentsBudgetsSum?: number;
  departmentOwner?: string;
  systemOwner?: string;
  globalBudget?: number;
  departmentBudget?: number;
  isRequest?: boolean; // bottomup vs topdown
  approverName?: string;
  readOnly?: boolean;
  status?: string;
  requestedBudget?: number;
  changeReason?: string;
  requestDate?: string;
  requesterName?: string;
  approverId?: number;
  requiredDepartmentOwnerId?: number;
  currentUserId?: number;
  currentUserIsAdmin?: boolean;
  currentUserIsSponsor?: boolean;
  canDecide?: boolean;
  onApprove?: (comment: string) => void;
  onReject?: (comment: string) => void;
  existingApproverComment?: string;
}

export interface BudgetChangeData {
  newAnnualBudget: number;
  reason: string;
  changeEffectiveDate: string;
}

export const BudgetChangeModal: React.FC<BudgetChangeModalProps> = ({
  open,
  onClose,
  onSubmit,
  budgetType,
  currentBudget,
  budgetCurrency,
  entityName,
  departmentsBudgetsSum,
  departmentOwner,
  systemOwner,
  globalBudget,
  departmentBudget,
  isRequest = false,
  approverName,
  readOnly = false,
  status,
  requestedBudget,
  changeReason,
  requestDate,
  requesterName,
  approverId,
  requiredDepartmentOwnerId,
  currentUserId,
  currentUserIsAdmin = false,
  currentUserIsSponsor = false,
  canDecide = false,
  onApprove,
  onReject,
  existingApproverComment,
}) => {
  const [newAnnualBudget, setNewAnnualBudget] = useState<string>('');
  const [reason, setReason] = useState<string>('');
  const [validationError, setValidationError] = useState<string>('');
  const [validationErrorOpen, setValidationErrorOpen] =
    useState<boolean>(false);
  const [fetchedApproverName, setFetchedApproverName] = useState<string>('');
  const [approverComment, setApproverComment] = useState<string>('');

  const isCurrentUserApprover =
    readOnly && status === 'Pending' && canDecide;

  const getTitle = () => {
    if (readOnly) {
      const action = isRequest ? 'Request' : 'Change';
      switch (budgetType) {
        case 'global':
          return `Global Budget ${action} Details`;
        case 'department':
          return `Department Budget ${action} Details`;
        case 'system':
          return `System Budget ${action} Details`;
        default:
          return `Budget ${action} Details`;
      }
    }

    const action = isRequest ? 'Request' : 'Change';
    switch (budgetType) {
      case 'global':
        return `Global Budget ${action}`;
      case 'department':
        return `Department Budget ${action}`;
      case 'system':
        return `System Budget ${action}`;
      default:
        return `Budget ${action}`;
    }
  };

  const getNewBudgetLabel = () => {
    switch (budgetType) {
      case 'global':
        return 'New Annual Global Budget';
      case 'department':
        return 'New Annual Department Budget';
      case 'system':
        return 'New Annual System Budget';
      default:
        return 'New Annual Budget';
    }
  };

  const getChangeEffectiveDate = () => {
    const today = new Date();
    const nextMonth = new Date(today.getFullYear(), today.getMonth() + 1, 1);
    return nextMonth.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const validateForm = () => {
    if (!newAnnualBudget.trim() || !reason.trim()) {
      setValidationError(
        `Provide New Annual ${
          budgetType === 'global'
            ? 'Global'
            : budgetType === 'department'
            ? 'Department'
            : 'System'
        } Budget value and Reason for change.`,
      );
      setValidationErrorOpen(true);
      return false;
    }

    const budgetValue = parseFloat(newAnnualBudget);
    if (isNaN(budgetValue) || budgetValue <= 0) {
      setValidationError('New Annual Budget must be a valid positive number.');
      setValidationErrorOpen(true);
      return false;
    }

    if (reason.length > 1000) {
      setValidationError('Reason for change cannot exceed 1000 characters.');
      setValidationErrorOpen(true);
      return false;
    }

    if (
      budgetType === 'global' &&
      departmentsBudgetsSum &&
      budgetValue < departmentsBudgetsSum
    ) {
      setValidationError(
        'New Annual Global Budget value results in sum of all Department Budgets greater than Global Budget. Align Department Budgets first and then implement change for the Global Budget.',
      );
      setValidationErrorOpen(true);
      return false;
    }

    if (
      budgetType === 'department' &&
      globalBudget &&
      departmentsBudgetsSum &&
      budgetValue + departmentsBudgetsSum - currentBudget > globalBudget
    ) {
      setValidationError(
        'New Annual Department Budget value results in sum of all Department Budgets greater than Global Budget. Change Annual Global Budget first and then implement change for the Department.',
      );
      setValidationErrorOpen(true);
      return false;
    }

    if (
      budgetType === 'system' &&
      departmentBudget &&
      departmentsBudgetsSum &&
      budgetValue + departmentsBudgetsSum - currentBudget > departmentBudget
    ) {
      setValidationError(
        'New Annual System Budget value results in sum of all System Budgets greater than Department Budget. Change Annual Department Budget first and then implement change for the System.',
      );
      setValidationErrorOpen(true);
      return false;
    }

    return true;
  };

  const handleSubmit = () => {
    if (validateForm()) {
      onSubmit?.({
        newAnnualBudget: parseFloat(newAnnualBudget),
        reason: reason.trim(),
        changeEffectiveDate: getChangeEffectiveDate(),
      });
      handleClose();
    }
  };

  const handleClose = () => {
    setNewAnnualBudget('');
    setReason('');
    setValidationError('');
    setValidationErrorOpen(false);
    setApproverComment('');
    onClose();
  };

  const handleApprove = () => {
    if (!approverComment.trim()) {
      setValidationError("Comment your decision in Approver's comment!");
      setValidationErrorOpen(true);
      return;
    }

    const newBudgetValue = requestedBudget || 0;

    if (budgetType === 'system') {
      if (departmentBudget && departmentsBudgetsSum !== undefined) {
        const totalSystemBudgets = departmentsBudgetsSum + newBudgetValue;
        if (totalSystemBudgets > departmentBudget) {
          setValidationError(
            'New Annual System Budget value results in sum of all System Budgets greater than Department Budget. Change Annual Department Budget first and then implement change for the System.',
          );
          setValidationErrorOpen(true);
          return;
        }
      }
    } else if (budgetType === 'department') {
      if (globalBudget && departmentsBudgetsSum !== undefined) {
        const totalDepartmentBudgets = departmentsBudgetsSum + newBudgetValue;
        if (totalDepartmentBudgets > globalBudget) {
          setValidationError(
            'New Annual Department Budget value results in sum of all Department Budgets greater than Global Budget. Change Annual Global Budget first and then implement change for the Department.',
          );
          setValidationErrorOpen(true);
          return;
        }
      }
    }

    onApprove?.(approverComment.trim());
    handleClose();
  };

  const handleReject = () => {
    if (!approverComment.trim()) {
      setValidationError("Comment your decision in Approver's comment!");
      setValidationErrorOpen(true);
      return;
    }

    onReject?.(approverComment.trim());
    handleClose();
  };

  useEffect(() => {
    if (!open) {
      setNewAnnualBudget('');
      setReason('');
      setValidationError('');
      setValidationErrorOpen(false);
      setFetchedApproverName('');
      setApproverComment('');
    } else if (readOnly && requestedBudget && changeReason) {
      setNewAnnualBudget(requestedBudget.toString());
      setReason(changeReason);
      if (existingApproverComment) {
        setApproverComment(existingApproverComment);
      }
    }
  }, [open, readOnly, requestedBudget, changeReason, existingApproverComment]);

  useEffect(() => {
    setFetchedApproverName(
      isRequest ? approverName ?? '' : departmentOwner ?? '',
    );

    if (readOnly && approverId && open) {
      const fetchApproverName = async () => {
        try {
          const users = await fetchWrapper.get(urls.DevOps.getUsersFilter);
          const approver = users.find((user: any) => user.id === approverId);

          if (approver) {
            setFetchedApproverName(
              `${approver.firstName} ${approver.lastName}`,
            );
          }
        } catch (error) {
          console.error('Error fetching approver name:', error);
          setFetchedApproverName('Unknown Approver');
        }
      };

      fetchApproverName();
    }
  }, [readOnly, approverId, open]);

  return (
    <>
      <Modal
        open={open}
        onClose={handleClose}
        closeAfterTransition
        slots={{
          backdrop: Backdrop,
        }}
        slotProps={{
          backdrop: {
            timeout: 500,
          },
        }}>
        <Fade in={open}>
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: 600,
              bgcolor: 'background.paper',
              boxShadow: '0 12px 40px rgba(16,35,58,.16)',
              p: 4,
              borderRadius: '14px',
              maxHeight: '90vh',
              overflow: 'auto',
            }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mb: 3,
              }}>
              <Typography variant="h6" component="h2">
                {getTitle()}
                {entityName && ` - ${entityName}`}
              </Typography>
              {status && (
                <Pill tone={statusTone(status)}>{status}</Pill>
              )}
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {budgetType === 'department' && (
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <TextField
                      label="Department Name"
                      variant="outlined"
                      fullWidth
                      value={entityName || ''}
                      disabled
                      InputProps={{
                        readOnly: true,
                      }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      label={isRequest ? 'Approver' : 'Department Owner'}
                      variant="outlined"
                      fullWidth
                      value={fetchedApproverName}
                      disabled
                      InputProps={{
                        readOnly: true,
                      }}
                    />
                  </Grid>
                </Grid>
              )}

              {budgetType === 'system' && (
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <TextField
                      label="System Name"
                      variant="outlined"
                      fullWidth
                      value={entityName || ''}
                      disabled
                      InputProps={{
                        readOnly: true,
                      }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      label={isRequest ? 'Approver' : 'System Owner'}
                      variant="outlined"
                      fullWidth
                      value={
                        readOnly && fetchedApproverName
                          ? fetchedApproverName
                          : isRequest
                          ? approverName
                          : systemOwner || ''
                      }
                      disabled
                      InputProps={{
                        readOnly: true,
                      }}
                    />
                  </Grid>
                </Grid>
              )}

              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    label={`Current Annual ${
                      budgetType === 'global'
                        ? 'Budget'
                        : budgetType === 'department'
                        ? 'Department Budget'
                        : 'System Budget'
                    }`}
                    variant="outlined"
                    fullWidth
                    value={currentBudget}
                    disabled
                    InputProps={{
                      readOnly: true,
                    }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="Budget Currency"
                    variant="outlined"
                    fullWidth
                    value={budgetCurrency}
                    disabled
                    InputProps={{
                      readOnly: true,
                    }}
                  />
                </Grid>
              </Grid>

              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    label={getNewBudgetLabel()}
                    variant="outlined"
                    fullWidth
                    type="number"
                    inputProps={{ min: 0, step: '0.01', 'data-testid': 'budget-change-new-annual-budget' }}
                    InputProps={{
                      readOnly: readOnly,
                    }}
                    value={newAnnualBudget}
                    onChange={
                      readOnly
                        ? undefined
                        : (e) => setNewAnnualBudget(e.target.value)
                    }
                    disabled={readOnly}
                    required={!readOnly}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label={readOnly ? 'Request Date' : 'Change Effective Date'}
                    variant="outlined"
                    fullWidth
                    value={
                      readOnly && requestDate
                        ? requestDate
                        : getChangeEffectiveDate()
                    }
                    disabled
                    InputProps={{
                      readOnly: true,
                    }}
                  />
                </Grid>
              </Grid>

              {readOnly && requesterName && (
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      label="Requester"
                      variant="outlined"
                      fullWidth
                      value={requesterName}
                      disabled
                      InputProps={{
                        readOnly: true,
                      }}
                    />
                  </Grid>
                </Grid>
              )}

              <TextField
                label="Reason for change"
                variant="outlined"
                fullWidth
                multiline
                rows={4}
                value={reason}
                onChange={
                  readOnly ? undefined : (e) => setReason(e.target.value)
                }
                disabled={readOnly}
                required={!readOnly}
                inputProps={{
                  maxLength: 1000,
                  readOnly: readOnly,
                  'data-testid': 'budget-change-reason',
                }}
                helperText={
                  readOnly ? undefined : `${reason.length}/1000 characters`
                }
              />

              {(isCurrentUserApprover || (readOnly && approverComment)) && (
                <TextField
                  label="Approver's comment"
                  variant="outlined"
                  fullWidth
                  multiline
                  rows={4}
                  value={approverComment}
                  onChange={
                    isCurrentUserApprover
                      ? (e) => setApproverComment(e.target.value)
                      : undefined
                  }
                  required={isCurrentUserApprover}
                  disabled={readOnly && !isCurrentUserApprover}
                  inputProps={{
                    maxLength: 1000,
                    readOnly: readOnly && !isCurrentUserApprover,
                    'data-testid': 'budget-change-approver-comment',
                  }}
                  helperText={
                    isCurrentUserApprover
                      ? `${approverComment.length}/1000 characters`
                      : undefined
                  }
                />
              )}

              <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                {isCurrentUserApprover ? (
                  <>
                    <Button
                      variant="outlined"
                      onClick={handleClose}
                      sx={{ flex: 1 }}>
                      Close
                    </Button>
                    <Button
                      variant="contained"
                      onClick={handleReject}
                      color="error"
                      sx={{ flex: 1 }}>
                      Reject
                    </Button>
                    <Button
                      variant="contained"
                      onClick={handleApprove}
                      color="success"
                      sx={{ flex: 1 }}
                      data-testid="budget-change-approve">
                      Approve
                    </Button>
                  </>
                ) : (
                  <>
                    <Button variant="outlined" onClick={handleClose} fullWidth>
                      {readOnly ? 'Close' : 'Cancel'}
                    </Button>
                    {!readOnly && onSubmit && (
                      <Button
                        variant="contained"
                        onClick={handleSubmit}
                        color="primary"
                        fullWidth
                        data-testid="budget-change-request-submit">
                        {isRequest
                          ? 'Request Budget Change'
                          : 'Submit Budget Change'}
                      </Button>
                    )}
                  </>
                )}
              </Box>
            </Box>
          </Box>
        </Fade>
      </Modal>

      <Dialog
        open={validationErrorOpen}
        onClose={() => setValidationErrorOpen(false)}
        aria-labelledby="validation-error-dialog-title"
        aria-describedby="validation-error-dialog-description">
        <DialogTitle id="validation-error-dialog-title">
          Budget Validation Error
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="validation-error-dialog-description">
            {validationError}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setValidationErrorOpen(false)}>OK</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
